import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { cryptoInfoService, CryptoInfoRec } from '../../../../services/cryptoInfo';
import { vaultService } from '../../../../services/vaultService';
import { tasksService } from '../../../../services/tasks';

import { createLogger } from '../../../../utils/logger';
import { useUserSelection } from '../../../../contexts/UserSelectionContext';
const logger = createLogger('CryptoInfoData');

type RouteParams = {
  targetUserId?: string;
  viewOnly?: boolean;
};

type InfoItemProps = {
  label: string;
  value: string;
};

const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => (
  <View style={styles.infoItem}>
    <Text style={styles.label}>{label}</Text>
    <Text style={styles.value}>{value || 'Not specified'}</Text>
    <View style={styles.separator} />
  </View>
);

export const CryptoInfoDataScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<RouteProp<Record<string, RouteParams>, string>>();
  const targetUserId = route.params?.targetUserId;
  const viewOnly = route.params?.viewOnly || false;
  // K5: a successor sees the owner's logins once the death is verified.
  const { isDeathVerifiedForSelectedUser } = useUserSelection();

  const [cryptoData, setCryptoData] = useState<CryptoInfoRec[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUsername, setShowUsername] = useState<{ [key: string]: boolean }>({});
  const [showPassword, setShowPassword] = useState<{ [key: string]: boolean }>({});
  const [showSecret, setShowSecret] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    loadCryptoData();
  }, [targetUserId]);

  const loadCryptoData = async () => {
    try {
      setLoading(true);
      const data = await cryptoInfoService.getCryptoInfo(targetUserId);
      logger.debug('[Crypto Data Debug] Loaded all crypto entries:', data);

      if (data && Array.isArray(data) && data.length > 0) {
        // Load portal credentials for all crypto entries (only for the owner, not viewOnly mode)
        const cryptoWithCredentials = await Promise.all(
          data.map(async (crypto) => {
            // Skip loading credentials if crypto.id is undefined
            if (!crypto.id) {
              logger.debug('[Portal Debug] Skipping crypto entry with undefined ID');
              return crypto;
            }

            // Skip loading portal credentials in viewOnly mode - they are protected until EOL
            if (!viewOnly || isDeathVerifiedForSelectedUser) {
              try {
                logger.debug('[Portal Debug] Attempting to load portal credentials for crypto ID:', crypto.id);
                const portalData = await vaultService.getPortalCredentials(crypto.id, 'crypto');
                // K5: the private key and seed phrase are stored (and decrypted)
                // with the login; they were never shown before.
                if (portalData && (portalData.username || portalData.password || portalData.privateKey || portalData.seedPhrase)) {
                  return {
                    ...crypto,
                    username: portalData.username,
                    password: portalData.password,
                    privateKey: portalData.privateKey,
                    seedPhrase: portalData.seedPhrase,
                  };
                }
              } catch (vaultError) {
                logger.debug('[Portal Debug] Error loading portal data for crypto:', crypto.id, vaultError);
              }
            }
            return crypto;
          })
        );

        logger.debug('[Crypto Data Debug] Setting all crypto data entries:', cryptoWithCredentials);
        setCryptoData(cryptoWithCredentials);
      } else {
        logger.debug('[Crypto Data Debug] No crypto entries found or invalid data format');
        setCryptoData([]);
      }
    } catch (error) {
      logger.error('Error loading crypto data:', error);
      Alert.alert('Error', 'Failed to load cryptocurrency information');
    } finally {
      setLoading(false);
    }
  };

  const handleEditCrypto = (crypto: CryptoInfoRec) => {
    navigation.navigate('CryptoInfoInput', {
      cryptoId: crypto.id,
      isEdit: true
    });
  };

  const handleDeleteCrypto = async (crypto: CryptoInfoRec) => {
    Alert.alert('Delete', 'Are you sure you want to delete this cryptocurrency entry?', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: async () => {
          try {
            await cryptoInfoService.deleteCryptoInfo(crypto.id);

            // Reload data after deletion
            await loadCryptoData();

            // Check if there are any remaining crypto entries
            const remainingData = await cryptoInfoService.getCryptoInfo();
            if (!remainingData || remainingData.length === 0) {
              await tasksService.resetTask('crypto');
              navigation.navigate('FinanceChecklist');
            }

            Alert.alert('Deleted', 'Cryptocurrency information deleted.');
          } catch (e) {
            logger.error('Delete crypto failed:', e);
            Alert.alert('Error', 'Failed to delete cryptocurrency information');
          }
        }
      },
    ]);
  };

  const handleAddCrypto = () => {
    navigation.navigate('CryptoInfoInput');
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>

        <Text style={styles.title}>Cryptocurrency Information</Text>

        <View style={styles.whiteContainer}>
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#29BFC0" />
            <Text style={styles.loadingText}>Loading cryptocurrency information...</Text>
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  if (!cryptoData || cryptoData.length === 0) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>

        <Text style={styles.title}>Cryptocurrency Information</Text>

        {viewOnly && (
          <View style={styles.viewOnlyBanner}>
            <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
          </View>
        )}

        <View style={styles.whiteContainer}>
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>
              {viewOnly ? 'This user has not added cryptocurrency information yet' : 'No cryptocurrency information found'}
            </Text>
            {!viewOnly && (
              <TouchableOpacity
                style={styles.addButton}
                onPress={handleAddCrypto}
              >
                <Text style={styles.addButtonText}>Add Crypto Info</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <TouchableOpacity style={styles.notificationButton}>
          <Image
            source={require('../../../../assets/icons/notification.png')}
            style={styles.notificationIcon}
          />
        </TouchableOpacity>
      </View>

      <Text style={styles.title}>Cryptocurrency Information</Text>

      {viewOnly && (
        <View style={styles.viewOnlyBanner}>
          <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
        </View>
      )}

      <View style={styles.whiteContainer}>
        <ScrollView style={styles.scrollView}>
          {cryptoData.map((crypto, index) => (
            <View key={crypto.id || index} style={styles.cryptoCard}>
              <View style={styles.cardHeader}>
                <View style={{flex: 1, flexShrink: 1}}>
                  <Text style={styles.cardName}>
                    {crypto.cryptocurrencyType || 'Cryptocurrency'}
                  </Text>
                  <Text style={styles.cardType}>
                    {crypto.exchangeName || crypto.walletType || 'Exchange not specified'}
                  </Text>
                </View>
                {!viewOnly && (
                  <View style={styles.cardButtons}>
                    <TouchableOpacity
                      style={styles.editCardButton}
                      onPress={() => handleEditCrypto(crypto)}
                    >
                      <Text style={styles.editCardButtonText}>Edit</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.deleteCardButton}
                      onPress={() => handleDeleteCrypto(crypto)}
                    >
                      <Text style={styles.deleteCardButtonText}>Delete</Text>
                    </TouchableOpacity>
                  </View>
                )}
              </View>

              <InfoItem label="Cryptocurrency Type" value={crypto.cryptocurrencyType || ''} />
              <InfoItem label="Exchange Name" value={crypto.exchangeName || ''} />
              <InfoItem label="Wallet Type" value={crypto.walletType || ''} />
              <InfoItem label="Wallet Address" value={crypto.walletAddress || ''} />
              <InfoItem label="Approximate Value" value={crypto.approximateValue ? `$${crypto.approximateValue}` : ''} />
              <InfoItem label="Purchase Date" value={crypto.purchaseDate || ''} />
              <InfoItem label="Purchase Price" value={crypto.purchasePrice ? `$${crypto.purchasePrice}` : ''} />
              <InfoItem label="Storage Location" value={crypto.storageLocation || ''} />
              <InfoItem label="Backup Location" value={crypto.backupLocation || ''} />

              {crypto.username && (
                <View style={styles.infoItem}>
                  <Text style={styles.label}>Username</Text>
                  <View style={styles.valueRow}>
                    <Text style={styles.value}>
                      {showUsername[crypto.id] ? crypto.username : '••••••••••'}
                    </Text>
                    <TouchableOpacity
                      onPress={() => setShowUsername(prev => ({ ...prev, [crypto.id]: !prev[crypto.id] }))}
                      style={styles.eyeButton}
                    >
                      <Image
                        source={showUsername[crypto.id]
                          ? require('../../../../assets/icons/Eye.png')
                          : require('../../../../assets/icons/Eye-closed.png')}
                        style={styles.eyeIcon}
                      />
                    </TouchableOpacity>
                  </View>
                  <View style={styles.separator} />
                </View>
              )}

              {crypto.password && (
                <View style={styles.infoItem}>
                  <Text style={styles.label}>Password</Text>
                  <View style={styles.valueRow}>
                    <Text style={styles.value}>
                      {showPassword[crypto.id] ? crypto.password : '••••••••••'}
                    </Text>
                    <TouchableOpacity
                      onPress={() => setShowPassword(prev => ({ ...prev, [crypto.id]: !prev[crypto.id] }))}
                      style={styles.eyeButton}
                    >
                      <Image
                        source={showPassword[crypto.id]
                          ? require('../../../../assets/icons/Eye.png')
                          : require('../../../../assets/icons/Eye-closed.png')}
                        style={styles.eyeIcon}
                      />
                    </TouchableOpacity>
                  </View>
                  <View style={styles.separator} />
                </View>
              )}

              {(['privateKey', 'seedPhrase'] as const).map((field) =>
                (crypto as any)[field] ? (
                  <View key={field} style={styles.infoItem}>
                    <Text style={styles.label}>{field === 'privateKey' ? 'Private Key' : 'Seed Phrase'}</Text>
                    <View style={styles.valueRow}>
                      <Text style={styles.value}>
                        {showSecret[crypto.id + field] ? (crypto as any)[field] : '••••••••••'}
                      </Text>
                      <TouchableOpacity
                        onPress={() => setShowSecret(prev => ({ ...prev, [crypto.id + field]: !prev[crypto.id + field] }))}
                        style={styles.eyeButton}
                      >
                        <Image
                          source={showSecret[crypto.id + field]
                            ? require('../../../../assets/icons/Eye.png')
                            : require('../../../../assets/icons/Eye-closed.png')}
                          style={styles.eyeIcon}
                        />
                      </TouchableOpacity>
                    </View>
                    <View style={styles.separator} />
                  </View>
                ) : null,
              )}

              <InfoItem label="Notes" value={crypto.notes || ''} />
            </View>
          ))}

          <View style={styles.buttonContainer}>
            {!viewOnly && (
              <TouchableOpacity
                style={styles.addAnotherCryptoButton}
                onPress={handleAddCrypto}
              >
                <Text style={styles.addAnotherCryptoButtonText}>Add Another Cryptocurrency</Text>
              </TouchableOpacity>
            )}

            <TouchableOpacity
              style={[styles.button, styles.backToListButton]}
              onPress={() => navigation.navigate('FinanceChecklist')}
            >
              <Text style={styles.buttonText}>Back to Checklist</Text>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '50%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
  },
  notificationIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
    textAlign: 'center',
    marginVertical: ms(20),
  },
  whiteContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(20),
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(40),
  },
  loadingText: {
    fontSize: ms(16, 0.3),
    color: '#29BFC0',
    marginTop: ms(10),
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(40),
  },
  emptyText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
    textAlign: 'center',
    marginBottom: ms(20),
  },
  addButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  addButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  cryptoCard: {
    backgroundColor: '#F8F9FA',
    borderRadius: ms(15),
    padding: ms(20),
    marginBottom: ms(20),
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: ms(20),
    paddingBottom: ms(15),
    borderBottomWidth: ms(2),
    borderBottomColor: '#29BFC0',
  },
  cardName: {
    fontSize: ms(20, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(4),
  },
  cardType: {
    fontSize: ms(16, 0.3),
    color: '#6B7280',
  },
  cardButtons: {
    flexDirection: 'row',
    gap: ms(8),
  },
  editCardButton: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    borderRadius: ms(15),
  },
  editCardButtonText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
  },
  deleteCardButton: {
    backgroundColor: '#DC3545',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    borderRadius: ms(15),
  },
  deleteCardButtonText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
  },
  infoItem: {
    marginBottom: ms(15),
  },
  label: {
    fontSize: ms(14, 0.3),
    color: '#6B7280',
    marginBottom: ms(5),
  },
  value: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  valueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  eyeButton: {
    padding: ms(5),
    marginLeft: ms(10),
  },
  eyeIcon: {
    width: s(16),
    height: s(16),
    tintColor: '#6B7280',
  },
  separator: {
    height: s(1),
    backgroundColor: '#E5E5E5',
  },
  buttonContainer: {
    padding: ms(20),
    paddingBottom: ms(120),
    gap: ms(15),
  },
  addAnotherCryptoButton: {
    backgroundColor: '#F2994A',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  addAnotherCryptoButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  button: {
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  backToListButton: {
    backgroundColor: '#29BFC0',
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  viewOnlyBanner: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    marginHorizontal: ms(20),
    borderRadius: ms(8),
    marginBottom: ms(10),
  },
  viewOnlyText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    textAlign: 'center',
  },
});
